import { Config, ApiEndpoint, MediaAssetFields, RelatedEntityItem, RelationshipsResponse } from "../types";
import { isCmTableEndpoint } from "./cmTableEndpoint";
import { toAsset } from "./mediaAsset";

// Type for relationship entities from the backend
interface RelationshipEntity {
  id: number | string; // Support both Django IDs and Fulcrum UUIDs
  name?: string;
  entity_name?: string;
  rel_description?: string;
  thumbnail?: string | null;
  media_type?: string;
  [key: string]: any;
}

/**
 * Table slugs that are never relationships and must not be shown as a
 * relationship column or a relationship chip:
 * - "restrictions" is a plain `restrictions: number[]` field on the row/media
 *   item, PATCHed directly rather than through anchor-relationships.
 * - "groups" is an access-control field.
 */
export const NON_RELATIONSHIP_TABLE_SLUGS = ["groups", "restrictions"];

/** Whether an item has at least one link (empty groups don't count). */
export const hasRelationships = (relationships: Partial<RelationshipsResponse> | undefined): boolean =>
  !!relationships &&
  (Object.values(relationships.byTableSlug ?? {}).some((items) => Array.isArray(items) && items.length > 0) ||
    (Array.isArray(relationships.media) && relationships.media.length > 0));

/** An item's links to `endpoint`: its media, or its related rows in that table. */
export const relatedItemsFor = (relationships: RelationshipsResponse, endpoint: ApiEndpoint): RelatedEntityItem[] =>
  (endpoint.entityType === "media" ? relationships.media : relationships.byTableSlug?.[endpoint.tableName]) ?? [];

/** `relationships` with its links to `endpoint` replaced by `items`. */
export const withLinksTo = (
  relationships: RelationshipsResponse | undefined,
  endpoint: ApiEndpoint,
  items: RelatedEntityItem[]
): RelationshipsResponse => {
  const current = { byTableSlug: relationships?.byTableSlug ?? {}, media: relationships?.media ?? [] };
  return endpoint.entityType === "media"
    ? { ...current, media: items }
    : { ...current, byTableSlug: { ...current.byTableSlug, [endpoint.tableName]: items } };
};

/**
 * Whether a relationship picker may offer "+ Add New" for this endpoint. Only CM
 * table rows and restrictions are created inline; media, spatial and tabular
 * datasets come from uploads, and Fulcrum records from Fulcrum.
 */
export const canAddNewFromPicker = (endpoint: ApiEndpoint | null | undefined): boolean =>
  isCmTableEndpoint(endpoint) || endpoint?.tableName === "restrictions";

/** The endpoint a relationship group belongs to: groups are keyed by the endpoint's `tableName`. */
export const findEndpointByKey = (endpoints: ApiEndpoint[], key: string): ApiEndpoint | undefined => {
  if (!endpoints || !key) return undefined;
  return endpoints.find((ep) => ep.tableName === key);
};

/**
 * Safely extract entity name from relationship entity, handling backend inconsistencies
 * Backend sometimes provides 'name', sometimes 'entity_name'
 */
export const getEntityName = (entity: RelationshipEntity): string => {
  return entity.name || entity.entity_name || `Item ${entity.id}`;
};

/**
 * Convert relationships to a flat array of display items: related rows in each
 * table, then media.
 */
export const flattenRelationshipsForDisplay = (
  relationships: any,
  config: Config
): Array<{
  key: string;
  id: string | number;
  name: string;
  isGeo: boolean;
  category: string;
}> => {
  if (!relationships) return [];

  const allItems: Array<{
    key: string;
    id: string | number;
    name: string;
    isGeo: boolean;
    category: string;
  }> = [];

  Object.entries(relationships.byTableSlug ?? {}).forEach(([slug, items]: [string, any]) => {
    if (!Array.isArray(items)) return;
    const matchingEndpoint = findEndpointByKey(config.apiEndpoints, slug);
    items.forEach((entity: RelationshipEntity) => {
      allItems.push({
        key: slug,
        id: entity.id,
        name: getEntityName(entity),
        isGeo: matchingEndpoint?.entityType === "geo" || !!(matchingEndpoint?.spatialType && matchingEndpoint.spatialType !== "none"),
        category: slug
      });
    });
  });

  if (Array.isArray(relationships.media)) {
    relationships.media.forEach((entity: RelationshipEntity) => {
      allItems.push({
        key: "media",
        id: entity.id,
        name: getEntityName(entity),
        isGeo: false,
        category: "media"
      });
    });
  }

  return allItems;
};

/** One item of the entity form's media strip: the API's asset fields, plus the link's own details. */
export interface MediaStripItem extends MediaAssetFields {
  id: number | string;
  name: string;
  /** The link's description (`rel_description`), which the strip shows under the item. */
  entity_description: string;
  media_type: string;
  is_primary?: boolean;
  relationship_id?: number;
  /** The original file, so an image can fall back to it while its thumbnail is written. */
  url: string;
}

/** The media strip's items, from an item's related media: the asset fields as sent, and the name resolved. */
export const extractMediaItems = (mediaRelationships: RelationshipEntity[]): MediaStripItem[] => {
  if (!Array.isArray(mediaRelationships)) return [];

  return mediaRelationships.map((media: RelationshipEntity) => ({
    ...toAsset(media),
    id: media.id,
    name: getEntityName(media),
    // The API's thumbnail; external-data related media carry theirs as entity_thumbnail.
    thumbnail: media?.thumbnail ?? (media as any)?.entity_thumbnail ?? null,
    entity_description: media.rel_description || "",
    media_type: media?.media_type || "",
    is_primary: (media as any)?.is_primary,
    relationship_id: (media as any)?.relationship_id,
    url: (media as any)?.url || (media as any)?.primary_image?.url || ""
  }));
};
